import React, { useState, useEffect, useRef } from 'react';
import { Recipe } from '@/types/recipe';
import { cn } from '@/lib/utils';
import { Coffee, Sun, ChefHat } from 'lucide-react';
import { getRecipeImageUrlCandidates } from '@/utils/recipeImageStorage';

interface RecipeImageProps {
  recipe: Pick<Recipe, 'title'> & Partial<Pick<
    Recipe,
    'tags' | 'user_image_path' | 'user_image_url' | 'storage_image_path' | 'storage_image_url' | 'image_url'
  >>;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  showFallback?: boolean;
  mealType?: 'breakfast' | 'lunch' | 'dinner';
  compact?: boolean;
}

const RecipeImage: React.FC<RecipeImageProps> = ({ 
  recipe, 
  className,
  size = 'md',
  showFallback = true,
  mealType,
  compact = false
}) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const imgRef = useRef<HTMLDivElement>(null);

  const sizeClasses = {
    sm: 'max-w-xs',
    md: 'max-w-md',
    lg: 'max-w-lg'
  };

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Load image directly without caching
  useEffect(() => {
    if (!isVisible) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const loadCandidate = (candidate: string) => {
      return new Promise<string>((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
          resolve(candidate);
        };

        img.onerror = () => {
          reject(new Error(`Failed to load recipe image: ${candidate}`));
        };

        img.src = candidate;
      });
    };

    const loadImage = async () => {
      const candidates = await getRecipeImageUrlCandidates({
        title: recipe.title,
        tags: recipe.tags,
        userImagePath: recipe.user_image_path,
        userImageUrl: recipe.user_image_url,
        storageImagePath: recipe.storage_image_path,
        storageImageUrl: recipe.storage_image_url,
        imageUrl: recipe.image_url,
      });

      for (const candidate of candidates) {
        try {
          const loadedSrc = await loadCandidate(candidate);
          if (!cancelled) {
            setImageSrc(loadedSrc);
            setIsLoading(false);
          }
          return;
        } catch (error) {
          console.warn(error);
        }
      }

      if (!cancelled) {
        setImageSrc(null);
        setIsLoading(false);
      }
    };

    loadImage();

    return () => {
      cancelled = true;
    };
  }, [
    isVisible,
    recipe.user_image_path,
    recipe.user_image_url,
    recipe.storage_image_path,
    recipe.storage_image_url,
    recipe.image_url,
    recipe.title,
    recipe.tags
  ]);

  // Get meal icon based on meal type
  const getMealIcon = () => {
    if (mealType === 'breakfast') {
      return <Coffee className="h-5 w-5 text-accent-foreground" />;
    } else if (mealType === 'lunch') {
      return <Sun className="h-5 w-5 text-primary-foreground" />;
    } else {
      return <ChefHat className="h-5 w-5 text-muted-foreground" />;
    }
  };

  const getMealIconBg = () => {
    if (mealType === 'breakfast') {
      return 'bg-accent/20';
    } else if (mealType === 'lunch') {
      return 'bg-primary/20';
    } else {
      return 'bg-muted/40';
    }
  };

  const hasImage = imageSrc && !isLoading;

  if (!hasImage && !showFallback) {
    return null;
  }

  // Compact mode for tight layouts
  if (compact) {
    return (
      <div ref={imgRef} className={cn("w-full", className)}>
        {isLoading ? (
          <div className="w-full aspect-square bg-muted animate-pulse rounded-md" />
        ) : hasImage ? (
          <img
            src={imageSrc}
            alt={recipe.title}
            className="w-full aspect-square object-cover rounded-md"
            loading="lazy"
          />
        ) : showFallback ? (
          <div className="w-full aspect-square flex items-center justify-center bg-gradient-to-br from-muted to-muted/60 rounded-md">
            <div className="text-center">
              <div className={`w-8 h-8 rounded-full ${getMealIconBg()} flex items-center justify-center mx-auto mb-1`}>
                {getMealIcon()}
              </div>
              <p className="text-xs text-muted-foreground font-medium">
                No image
              </p>
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  // Standard mode - use CSS aspect-ratio instead of AspectRatio component
  return (
    <div ref={imgRef} className={cn("mx-auto", sizeClasses[size], className)}>
      <div className="w-full overflow-hidden rounded-lg border border-border bg-muted" style={{ aspectRatio: '4/3' }}>
        {isLoading ? (
          <div className="w-full h-full bg-muted animate-pulse rounded-lg" />
        ) : hasImage ? (
          <img
            src={imageSrc}
            alt={recipe.title}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : showFallback ? (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted/60">
            <div className="text-center">
              <div className={`w-16 h-16 rounded-full ${getMealIconBg()} flex items-center justify-center mx-auto mb-2`}>
                {getMealIcon()}
              </div>
              <p className="text-xs text-muted-foreground font-medium">
                No image
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default RecipeImage;
